/**
 * Format location data from the API to a format usable by the application.
 *
 * @param {Array<Object>} data - Location data from API.
 * @returns {Array<Object>} - Array of formatted data.
 */
export const formatLocationData = data => {
  const formatted = [];

  data.forEach(location => {
    try {
      const {
        location_type: types,
        location_id: id,
        title,
        open_soon: upcoming,
        latitude: lat,
        longitude: lng,
      } = location;

      if (types && id && upcoming && lat && lng) {
        formatted.push({
          types,
          id,
          title,
          upcoming: upcoming === '1',
          lat: parseFloat(lat),
          lng: parseFloat(lng),
        });
      }
    } catch (e) {
      // Error adding location.
    }
  });

  return formatted;
};

/**
 * For the provided bounds, returns the center lat and lng.
 *
 * @param {Object} bounds - Bounds to find center of
 * @return {Object} - Lat and lng of the provided bounds.
 */
export const getCenterFromBounds = bounds => {
  const { northeast, southwest } = bounds;

  const lat = (northeast.lat + southwest.lat) / 2;
  const lng = (northeast.lng + southwest.lng) / 2;

  return {
    lat,
    lng,
  };
};

/**
 * Indicates if the provided location is within the provided bounds.
 *
 * @param {Object} location - An object of lat / lng coords for a location.
 * @param {Object} bounds - An object of lat / lng bounds.
 * @return {Boolean} - Whether or not the location is within the provided bounds.
 */
const isLocationWithinBounds = (location, bounds) => {
  const { northeast: ne, southwest: sw } = bounds;

  if (location.lat > ne.lat || location.lat < sw.lat) return false;

  // Check longitude.
  const eastWithinBounds = location.lng <= ne.lng;
  const westWithinBounds = location.lng >= sw.lng;

  if (sw.lng > ne.lng) {
    return eastWithinBounds || westWithinBounds;
  }

  return eastWithinBounds && westWithinBounds;
};

/**
 * Determines if the first provided bounds contains the second provided bounds.
 *
 * Each bounds takes the structure:
 * {
 *    northeast: { lat: ..., lng: ... },
 *    southwest: { lat: ..., lng: ... },
 * }
 *
 * @param {Object} containing - The "containing" bounds.
 * @param {Object} contained - The "contained" bounds.
 * @return {Boolean} - Indicates whether the "containing" bound contains the "contained" bounds
 */
const containsBounds = (containing, contained) => {
  const northeastContained = isLocationWithinBounds(contained.northeast, containing);
  const southwestContained = isLocationWithinBounds(contained.southwest, containing);

  return northeastContained && southwestContained;
};

/**
 * Gets locations within the provided bounds.
 *
 * @param {Array<Object>} locations - Array of locations
 * @param {Object} bounds - Bounds to retrieve locations within.
 * @return {Array<Object>} - Returns locations.
 */
const getLocationsWithinBounds = (locations, bounds) =>
  locations.filter(location => {
    const point = {
      lat: location.lat,
      lng: location.lng,
    };

    return isLocationWithinBounds(point, bounds);
  });

/**
 * Given the requested bounds and filters, pulls as many locations from cache as possible.
 * Identifies the location bounds and filters that the cache does not have.
 *
 * This model can be extended for more advanced caching, for example, recognizing partial cache
 * hits and recognizing the data that must be requested to fill the gaps.
 *
 * @param {Object} bounds - Requested location bounds
 * @param {Array<String>} filters - Requested location filters
 * @param {Object} cache - Locations cache
 * @return {Array<Object>} - An array of objects that have the bounds / filters combinations that
 *    are not found in the cache. Each item will have the structure:
 *    {
 *      bounds: { ... },
 *      filters: [ ... ],
 *    }
 */
export const retrieveFromCache = (bounds, filters, cache) => {
  // Locations from cache.
  let locations = [];

  // Filters whose locations have not been cached for the provided bounds.
  const uncachedFilters = [];

  // Iterate through the cache for each filter.
  filters.forEach(filter => {
    const filterCache = cache[filter];
    let hasCachedData = false;

    if (filterCache) {
      // For each item in the filter cache, check if the requested bounds are contained
      // within the cache item's bounds.
      //
      // This can be extended to check for and retrieve partial cache hits.
      filterCache.some(item => {
        if (containsBounds(item.bounds, bounds)) {
          locations = locations.concat(getLocationsWithinBounds(item.locations, bounds));
          hasCachedData = true;
        }

        return hasCachedData;
      });
    }

    // Add this filter to the list of remaining filters to request.
    if (!hasCachedData) uncachedFilters.push(filter);
  });

  // If we don't have cache values for some of the filters, then identify those filters as the
  // "remainder" to be requested.
  const remaining = [];

  if (uncachedFilters.length > 0) {
    remaining.push({
      bounds,
      filters: uncachedFilters,
    });
  }

  return {
    locations,
    remaining,
  };
};

/**
 * Clones and returns an updated locations cache with the provided locations.
 *
 * @param {Object} bounds - Bounds to cache location items for.
 * @param {Array<String>} filters - Filters to cache.
 * @param {Array<Object>} locations - Locations to cache.
 * @param {Object} cache - Existing locations cache.
 * @return {Object} - Updated cache.
 */
export const getUpdatedLocationsCache = (bounds, filters, locations, cache) => {
  const updatedCache = Object.assign({}, cache);

  filters.forEach(filter => {
    if (!updatedCache[filter]) updatedCache[filter] = [];

    updatedCache[filter].push({
      bounds,
      locations: locations.filter(location => location.types.includes(filter)),
    });
  });

  return updatedCache;
};
